import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@forbiz/web-kit'
import type { OrderListItem, VendorSettlementItem } from '@forbiz/contracts'

import { ordersApi } from '../lib/orders.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'

/**
 * 업체 정산 조회(읽기전용, BIZ-16 파생). 자기 업체의 배송완료 주문을 청구월 단위로 모아
 * 공급가와 지급 상태를 보여준다. 지급 실행·묶음 생성은 운영자 몫이라 여기선 조회만.
 * 상단 요약 스트립(배정 주문과 같은 vsum) + 월 이동 + 주문별 정산 행.
 */

const occasionLabel: Record<OrderListItem['occasionType'], string> = {
  FUNERAL: '근조',
  CELEBRATION: '축하',
  GIFT: '난·기타',
}

const occasionClass: Record<OrderListItem['occasionType'], string | undefined> = {
  FUNERAL: styles.occFuneral,
  CELEBRATION: styles.occCelebrate,
  GIFT: styles.occGift,
}

/** 정산 상태 → 칩 라벨·색. 업체 관점 문구(대기/예정/완료). */
function settlementChip(status: VendorSettlementItem['status']): { label: string; cls: string | undefined } {
  switch (status) {
    case 'PAID':
      return { label: '지급 완료', cls: styles.chipSuccess }
    case 'PLANNED':
      return { label: '지급 예정', cls: styles.chipWarning }
    default:
      // 대기는 무채색 — 이 팔레트에선 info가 success와 같은 초록이라 '받은 돈'과 구분이 안 된다.
      return { label: '정산 대기', cls: undefined }
  }
}

function fmtWon(n: number): string {
  return `${n.toLocaleString('ko-KR')}원`
}

/** 지급 여부 필터. 업체 관점 두 버킷 — 받을 돈(미지급) / 받은 돈(지급완료). */
const PAY_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'unpaid', label: '미지급' },
  { value: 'paid', label: '지급완료' },
]

/** 필터 값 → 항목 포함 여부. 미지급 = 대기·예정, 지급완료 = PAID. */
function matchesPayFilter(status: VendorSettlementItem['status'], filter: string): boolean {
  if (filter === 'unpaid') return status === 'PENDING' || status === 'PLANNED'
  if (filter === 'paid') return status === 'PAID'
  return true
}

/** YYYY-MM → "2026년 9월". */
function monthLabel(m: string): string {
  const [y, mo] = m.split('-')
  return `${y}년 ${Number(mo)}월`
}

/** 현재 KST 청구월 — 서버 청구월 축과 같게, 브라우저 시간대와 무관하게 KST로 잡는다. */
function currentMonth(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()).slice(0, 7)
}

/** month(YYYY-MM)에 delta개월 더한 값. */
function shiftMonth(m: string, delta: number): string {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(y!, mo! - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** 배송 완료 시각 — 목록엔 짧게 MM.DD. */
function fmtDay(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

const CHEVRON_L = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const CHEVRON_R = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** 요약 셀의 숫자 한 칸 — 숫자는 같은 크기·잉크색, 단위(건·원)만 작은 첨자로. */
function Metric({ value, unit }: { value: number | undefined; unit: string }) {
  if (value === undefined) return <div className={styles.vsumNum}>—</div>
  return (
    <div className={styles.vsumNum}>
      {value.toLocaleString('ko-KR')}
      <span className={styles.vsumUnit}>{unit}</span>
    </div>
  )
}

export function SettlementPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isVendor = user?.role === 'VENDOR_MEMBER'
  const [month, setMonth] = useState(currentMonth())
  const [payFilter, setPayFilter] = useState('')
  const atCurrent = month >= currentMonth() // 미래월로는 못 넘어간다

  // 업체가 아니면 요청 자체를 안 보낸다(URL 직접 진입 시 403 호출 방지).
  const { data, loading, error } = useAsync(
    () => (isVendor ? ordersApi.vendorSettlement(month) : Promise.resolve(null)),
    [month, isVendor],
  )

  if (!isVendor) {
    return (
      <main>
        <div className={styles.header}>
          <h1 className={styles.title}>정산</h1>
        </div>
        <div className={styles.empty}>업체 담당자만 볼 수 있어요</div>
      </main>
    )
  }

  // 월 이동 중·실패 시 이전 달 숫자가 새 달 라벨 아래 남지 않게, 지금 보는 달의 응답만 쓴다.
  const cur = !loading && !error && data?.month === month ? data : null
  const s = cur?.summary
  const items = cur?.items ?? []
  const shown = items.filter((it) => matchesPayFilter(it.status, payFilter))

  return (
    <main>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>정산</h1>
          <p className={styles.subtitle}>배송 완료 주문의 공급가·지급 현황</p>
        </div>
      </div>

      {/* 월 이동 — 이전/현재/다음. 미래월은 막는다(정산 대상이 아직 없음). */}
      <div className={styles.monthNav}>
        <button
          type="button"
          className={styles.monthBtn}
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="이전 달"
        >
          {CHEVRON_L}
        </button>
        <span className={styles.monthLabel}>{monthLabel(month)}</span>
        <button
          type="button"
          className={styles.monthBtn}
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          disabled={atCurrent}
          aria-label="다음 달"
        >
          {CHEVRON_R}
        </button>
      </div>

      {/* 요약 스트립 — 세 숫자를 같은 크기·잉크색으로 통일(단위 건·원은 작은 첨자).
          카운트와 금액이 섞여도 한 세트로 읽히게. compact 변형(배정 주문 요약과 별도 크기). */}
      <div className={`${styles.vsum} ${styles.vsumCompact}`}>
        <div className={styles.vsumCell}>
          <Metric value={s?.count} unit="건" />
          <div className={styles.vsumLbl}>배송 완료</div>
        </div>
        <div className={styles.vsumCell}>
          <Metric value={s?.pendingAmount} unit="원" />
          <div className={styles.vsumLbl}>정산 예정</div>
        </div>
        <div className={styles.vsumCell}>
          <Metric value={s?.paidAmount} unit="원" />
          <div className={styles.vsumLbl}>지급 완료</div>
        </div>
      </div>

      {/* 지급 여부 필터 — 주문이 있을 때만(0건이면 소음). */}
      {items.length > 0 ? (
        <div className={`${styles.segmented} ${styles.setlFilter}`} role="group" aria-label="지급 여부 필터">
          {PAY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={payFilter === f.value}
              className={payFilter === f.value ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn}
              onClick={() => setPayFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : items.length === 0 ? (
        <div className={styles.empty}>이 달 정산 대상 주문이 없어요</div>
      ) : shown.length === 0 ? (
        <div className={styles.empty}>
          {payFilter === 'unpaid' ? '미지급 주문이 없어요' : '지급 완료된 주문이 없어요'}
        </div>
      ) : (
        <>
          <div className={styles.setlList}>
            {shown.map((it) => {
              const chip = settlementChip(it.status)
              const go = () => navigate(`/orders/${it.orderId}`)
              return (
                <div
                  key={it.orderId}
                  className={styles.setlRow}
                  onClick={go}
                  role="link"
                  tabIndex={0}
                  aria-label={`주문 ${it.orderNo ?? ''} 상세 보기`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      go()
                    }
                  }}
                >
                  <div className={styles.setlMain}>
                    <div className={styles.setlTop}>
                      <span className={`${styles.occ} ${occasionClass[it.occasionType]}`}>
                        {occasionLabel[it.occasionType]}
                      </span>
                      <span className={styles.setlDate}>{fmtDay(it.deliveredAt)} 완료</span>
                    </div>
                    <div className={styles.setlTitle}>
                      {it.productName}
                      {it.quantity > 1 ? <span className={styles.setlQty}> × {it.quantity}</span> : null}
                    </div>
                  </div>
                  <div className={styles.setlRight}>
                    <div className={styles.setlAmount}>{fmtWon(it.payable)}</div>
                    <span className={chip.cls ? `${styles.chip} ${chip.cls}` : styles.chip}>{chip.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
          {/* 합계 — 필터가 걸리면 보이는 것만, 전체면 공급가 총액. 오해 없게 라벨을 바꾼다. */}
          {s ? (
            <div className={styles.setlTotal}>
              <span className={styles.muted}>{payFilter === '' ? '공급가 합계' : '선택 합계'}</span>
              <span className={styles.setlTotalNum}>
                {fmtWon(
                  payFilter === '' ? s.totalPayable : shown.reduce((sum, it) => sum + it.payable, 0),
                )}
              </span>
            </div>
          ) : null}
        </>
      )}
    </main>
  )
}
